import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Switch,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

export const userPromptTemplateCreatorFragment = graphql(`
  fragment UserPromptTemplateCreator on Users @_unmask {
    __typename
    id
    isPromptTemplateCreator
  }
`);

export const userTemplatePublisherFragment = graphql(`
  fragment UserTemplatePublisher on Users @_unmask {
    __typename
    id
    isTemplatePublisher
  }
`);

const updateUserPromptTemplateCreatorMutation = graphql(
  `
    mutation updateUserPromptTemplateCreator(
      $userId: uuid!
      $isPromptTemplateCreator: Boolean
    ) {
      updateUsersByPk(
        pkColumns: { id: $userId }
        _set: { isPromptTemplateCreator: $isPromptTemplateCreator }
      ) {
        ...UserPromptTemplateCreator
      }
    }
  `,
  [userPromptTemplateCreatorFragment],
);

const updateUserTemplatePublisherMutation = graphql(
  `
    mutation updateUserTemplatePublisher(
      $userId: uuid!
      $isTemplatePublisher: Boolean
    ) {
      updateUsersByPk(
        pkColumns: { id: $userId }
        _set: { isTemplatePublisher: $isTemplatePublisher }
      ) {
        ...UserTemplatePublisher
      }
    }
  `,
  [userTemplatePublisherFragment],
);

interface BaseToggleProps {
  userId: string;
}

export const UserTemplateCreatorToggle: React.FC<BaseToggleProps> = ({
  userId,
}) => {
  const [nextState, setNextState] = useState<boolean | undefined>(undefined);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const toast = useToast();

  const [updateUserPromptTemplateCreator] = useMutation(
    updateUserPromptTemplateCreatorMutation,
    {
      onError: () => toast.error('Failed to update user permissions'),
      optimisticResponse: (data) => ({
        updateUsersByPk: {
          __typename: 'Users' as const,
          id: userId,
          isPromptTemplateCreator: data.isPromptTemplateCreator ?? false,
        },
      }),
    },
  );

  const handleSave = () => {
    if (nextState === undefined) {
      return;
    }

    updateUserPromptTemplateCreator({
      variables: {
        userId,
        isPromptTemplateCreator: nextState,
      },
    });

    setIsConfirmationDialogOpen(false);
    setNextState(undefined);
  };

  const user = useCompleteFragment({
    fragment: userPromptTemplateCreatorFragment,
    key: {
      id: userId,
    },
  });

  const cancelChange = () => {
    setIsConfirmationDialogOpen(false);
    setNextState(undefined);
  };

  return (
    <>
      <Dialog
        open={isConfirmationDialogOpen}
        onOpenChange={(open) => {
          if (open === false) {
            cancelChange();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {match(nextState)
                .with(true, () => 'Enable Template Creator')
                .otherwise(() => 'Disable Template Creator')}
            </DialogTitle>
            <DialogDescription>
              {match(nextState)
                .with(
                  true,
                  () => 'This user will be able to create prompt templates.',
                )
                .otherwise(
                  () =>
                    'This user will no longer be able to create prompt templates.',
                )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelChange}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Okay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Switch
        onCheckedChange={(checked) => {
          setNextState(checked);
          setIsConfirmationDialogOpen(true);
        }}
        checked={user.isPromptTemplateCreator}
        aria-label="Is Template Creator"
      />
    </>
  );
};

export const UserTemplatePublisherToggle: React.FC<BaseToggleProps> = ({
  userId,
}) => {
  const [nextState, setNextState] = useState<boolean | undefined>(undefined);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const toast = useToast();

  const [updateUserTemplatePublisher] = useMutation(
    updateUserTemplatePublisherMutation,
    {
      onError: () => toast.error('Failed to update user permissions'),
      optimisticResponse: (data) => ({
        updateUsersByPk: {
          __typename: 'Users' as const,
          id: userId,
          isTemplatePublisher: data.isTemplatePublisher ?? false,
        },
      }),
    },
  );

  const handleSave = () => {
    if (nextState === undefined) {
      return;
    }

    updateUserTemplatePublisher({
      variables: {
        userId,
        isTemplatePublisher: nextState,
      },
    });

    setIsConfirmationDialogOpen(false);
    setNextState(undefined);
  };

  const user = useCompleteFragment({
    fragment: userTemplatePublisherFragment,
    key: {
      id: userId,
    },
  });

  const cancelChange = () => {
    setIsConfirmationDialogOpen(false);
    setNextState(undefined);
  };

  return (
    <>
      <Dialog
        open={isConfirmationDialogOpen}
        onOpenChange={(open) => {
          if (open === false) {
            cancelChange();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {match(nextState)
                .with(true, () => 'Enable Template Publisher')
                .otherwise(() => 'Disable Template Publisher')}
            </DialogTitle>
            <DialogDescription>
              {match(nextState)
                .with(
                  true,
                  () => 'This user will be able to publish templates.',
                )
                .otherwise(
                  () =>
                    'This user will no longer be able to publish templates.',
                )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelChange}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Okay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Switch
        onCheckedChange={(checked) => {
          setNextState(checked);
          setIsConfirmationDialogOpen(true);
        }}
        checked={user.isTemplatePublisher}
        aria-label="Is Template Publisher"
      />
    </>
  );
};
